import React, { useEffect, useState } from 'react'
import queryString from 'query-string'
import { UPGRADE_TIER_API_PATH } from '../../constants/routes'
import { createPammFund, getUserDetails } from '../api'
import { useTranslation } from 'react-i18next'
import { useStore } from '../../StateProvider'
import { useLocation } from 'react-router-dom'
import { Page } from '../../ui/Page/Page'
import { isEmpty, isNil } from 'ramda'
import { getExternalFieldsList, getIsExtFldsValid, sortExtFlds } from '../TierForm/tierFormUtils'
import { FormWithoutSteps } from '../TierForm/FormWithoutSteps/FormWithoutSteps'
import { additionalFldValues, createStoreValidationByTier } from '../ExternalFields/utils'
import { Message } from '../../ui'
import { ErrorMessage } from '../ErrorMessage/ErrorMessage'

export const pammExtFieldsDescription = [
  {
    i18n: 'fundDistType',
    name: 'fundDistType',
    order: 1,
    required: true,
    type: 'select'
  },
  {
    i18n: 'fundName',
    name: 'fundName',
    order: 2,
    required: true,
    type: 'text'
  },
  {
    i18n: 'fundPerformFee',
    name: 'fundPerformFee',
    order: 3,
    required: true,
    type: 'masked',
    maxLength: 6
  },
  {
    i18n: 'fundPerformFeePeriod',
    name: 'fundPerformFeePeriod',
    order: 4,
    required: true,
    type: 'feePeriodSelect'
  },
  {
    i18n: 'fundMgmtFee',
    name: 'fundMgmtFee',
    order: 5,
    required: true,
    type: 'masked',
    maxLength: 6
  },
  {
    i18n: 'fundMgmtFeePeriod',
    name: 'fundMgmtFeePeriod',
    order: 6,
    required: true,
    type: 'feePeriodSelect'
  },
  {
    i18n: 'commissionType',
    name: 'commissionType',
    order: 7,
    required: true,
    type: 'select'
  },
  {
    i18n: 'commissionUnits',
    name: 'commissionUnits',
    order: 8,
    required: true,
    type: 'commissionUnitsSelect'
  },
  {
    i18n: 'commissionAmount',
    name: 'commissionAmount',
    order: 9,
    required: true,
    type: 'masked'
  },
  {
    i18n: 'fractionalPrecision',
    name: 'fractionalPrecision',
    order: 10,
    required: false,
    type: 'masked',
    maxLength: 8,
    info: 'fractionalPrecision'
  }
]

const CreatePammFund = () => {
  const { t } = useTranslation()
  const [error, setError] = useState({})
  const [fields, setFields] = useState([])
  const [isValid, setIsValid] = useState(false)
  const [isLoadingSubmit, setIsLoadingSubmit] = useState(false)
  const [isCreated, setIsCreated] = useState(false)

  const [{ validation, validation: { tier }, form, form: { accId }, user }, dispatch] = useStore()
  const location = useLocation()

  useEffect(() => {
    const { token = '' } = queryString.parse(location.search)
    const url = `${UPGRADE_TIER_API_PATH}?token=${token}`

    getUserDetails(url).then(
      /**
       * @param {Object} user
       */
      (user) => {
        dispatch({ type: 'setTier', tier: user.tier })
        dispatch({ type: 'setAccId', accId: 0 })
        dispatch({ type: 'setUser', user })
        if (user?.processingMode !== 'M') {
          setError({ message: t('createPammFund.masterAccountError') })
        }
      },
      /**
       * @param {ErrorCommon} error
       */
      (error) => {
        setError(error)
      }
    )
  }, [])

  useEffect(() => {
    if (isNil(tier)) return
    dispatchSetValidation()
  }, [tier])

  useEffect(() => {
    if (isEmpty(pammExtFieldsDescription) || !tier) {
      return
    }
    setIsValid(getIsExtFldsValid(validation[`tier${tier}`][accId], pammExtFieldsDescription))
  }, [validation, tier, accId])

  useEffect(() => {
    const flds = sortExtFlds(getExternalFieldsList(pammExtFieldsDescription))
    setFields(flds)
  }, [tier])

  const dispatchSetValidation = () => {
    const pammExtFieldsDescriptionWithTier = pammExtFieldsDescription.map(
      fldDescr => ({ ...fldDescr, tier })
    )
    const validation = createStoreValidationByTier(
      pammExtFieldsDescriptionWithTier, tier
    )
    dispatch({
      type: 'setTierValidation',
      tier,
      validation
    })
  }

  const onFormSubmit = () => {
    setIsLoadingSubmit(true)
    createPammFund(additionalFldValues(form[accId])(pammExtFieldsDescription))
      .then(
        () => {
          setIsCreated(true)
          setIsLoadingSubmit(false)
        },
        (error) => {
          setError(error)
          setIsCreated(false)
          setIsLoadingSubmit(false)
        }
      )
  }

  return (
    <>
      {isEmpty(error) && !isCreated && (
        <Page caption={t('createPammFund.title')} text={t('createAccount.allRequired')}>
          {!isEmpty(user) && (
            <FormWithoutSteps
              fields={fields}
              isButtonDisabled={!isValid || isLoadingSubmit}
              onSubmit={onFormSubmit}
              isLoadingSubmit={false}
              buttonName={t('createPammFund.submitName')}
            />)}
        </Page>)}
      {!isEmpty(error) && <ErrorMessage error={{ message: error.message }} />}
      {isCreated && <Message title={t('createPammFund.success')} type='success' />}
    </>
  )
}

export { CreatePammFund }
